// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import * as R from "ramda";
import { useDebouncedCallback } from "use-debounce";

import { Filter as StyledFilter, Chatroom as StyledWorkflow } from "./styles";
import { searchWorkflow } from "src/actions/workflows";
import { setForm } from "src/actions/forms";
import Checkbox from "src/components/Checkbox";
import Name from "src/containers/workflow/Name";
import { getSearchUniqueValues, getWorkflowInstanceFilter } from "src/reducers";
import { Input } from "src/styles/input";
import { Separator } from "src/components/Manage/Workflow/Instances/Table/Filter/styles";
import SortAndFilter from "./SortAndFilter";

import type { AppState, WorkflowInstanceFilter } from "src/types";

type Props = {
  column: string,
  values: Array<number>,
  parentRef: any,
  filter: WorkflowInstanceFilter,
  _setForm: Function,
  handleClose: Function,
  _searchWorkflow: Function
};

const Workflow = ({
  parentRef,
  values,
  filter,
  column,
  _setForm,
  handleClose,
  _searchWorkflow
}: Props) => {
  const [search, setSearch] = useState("");
  const [debouncedSearchWorkflow] = useDebouncedCallback(_searchWorkflow, 800);

  useEffect(() => {
    _searchWorkflow("", {
      searchType: "uniqueWorkflow",
      column: `${column}`
    });
    return () => {
      _searchWorkflow("", {
        searchType: "uniqueWorkflow",
        column: `${column}`
      });
    };
  }, []);

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSearch = useCallback(
    (event: any) => {
      preventDefault(event);
      setSearch(event.target.value);
      debouncedSearchWorkflow(event.target.value, {
        searchType: "uniqueWorkflow",
        column: `${column}`
      });
    },
    [setSearch, debouncedSearchWorkflow]
  );

  const closeModal =
    ((event: any) => {
      if (event.keyCode === 13) {
        handleClose();
      }
    },
    [handleClose]);

  const clickOutside = useCallback(
    (event: any) => {
      if (parentRef && parentRef.current) {
        if (!parentRef.current.contains(event.target)) {
          handleClose();
        }
      }
    },
    [parentRef, handleClose]
  );

  useEffect(() => {
    // $FlowFixMe
    document.addEventListener("keydown", closeModal, false);
    document.addEventListener("click", clickOutside, false);
    return () => {
      // $FlowFixMe
      document.removeEventListener("keydown", closeModal, false);
      document.removeEventListener("click", clickOutside, false);
    };
  }, []);

  const handleSelect = useCallback(
    (event: any, item: string | number) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (R.includes(item, filter[column] || [])) {
        _setForm({
          query: {
            ...filter,
            [column]: R.reject(R.equals(item), filter[column]),
            page: 1
          }
        });
      } else {
        _setForm({
          query: {
            ...filter,
            [column]: [...(filter[column] || []), item],
            page: 1
          }
        });
      }
    },
    [column, filter, _setForm]
  );

  const workflowFilter = filter[column] || [];

  return (
    <StyledFilter onClick={preventDefault}>
      <SortAndFilter column={column} withoutBox filterShown />
      <Separator />
      <Input
        type="text"
        value={search}
        onChange={handleSearch}
        placeholder={i18n.t(k.SEARCH)}
        autoFocus
      />

      <ul>
        {values.map(value => (
          <StyledWorkflow
            tabIndex="0"
            role="button"
            key={value}
            onClick={event => handleSelect(event, value)}
          >
            <Checkbox
              id={`${"workflow"}${value}`}
              checked={R.includes(value, workflowFilter)}
              handleChange={event => handleSelect(event, value)}
            />

            <Name id={value} />
          </StyledWorkflow>
        ))}
      </ul>
    </StyledFilter>
  );
};

const mapStateToProps = ({ app }: { app: AppState }, props) => ({
  values: getSearchUniqueValues(app, props.column),
  filter: getWorkflowInstanceFilter(app)
});

export default connect(mapStateToProps, {
  _setForm: setForm,
  _searchWorkflow: searchWorkflow
})(Workflow);
